import {
	TextInput,
	NumberInput,
	MultiSelect,
	Checkbox,
	Button,
	Group,
	Title,
	Card,
	Stack,
	Divider,
	Text,
	Box,
	Loader,
	Image,
	Modal,
	Alert,
} from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { useDisclosure } from "@mantine/hooks";
import { isEmpty } from "lodash";
import { useState, useEffect } from "react";
import { HiOutlineUpload } from "react-icons/hi";
import { ProductFormComponentProps } from "types/module/ProductSubmission";
import { RichTextEditor } from "components/ui/RichTextEditor/RichTextEditor";

const ProductForm: React.FC<ProductFormComponentProps> = ({
	form,
	handleSubmit,
	loading = false,
	error,
	setError,
	productCategories,
	subCategories,
	fieldRefs,
	loadingCategories,
	categoryError,
	subCategoryError,
}) => {
	const [modalImage, setModalImage] = useState<string | null>(null);
	const [opened, { open, close }] = useDisclosure(false);
	const [zoomed, setZoomed] = useState(false);
	const [modalImageName, setModalImageName] = useState<string | null>(null);

	const [additionalImagesError, setAdditionalImagesError] = useState<
		string | null
	>(null);

	// Auto-dismiss error after 3 seconds
	useEffect(() => {
		if (error) {
			const timer = setTimeout(() => {
				setError(null);
			}, 3000);

			// Cleanup timer if component unmounts or error changes
			return () => clearTimeout(timer);
		}
	}, [error, setError]);

	const getPreviewUrl = (file: File) => URL.createObjectURL(file);

	return (
		<>
			<Card shadow="md" p="lg" radius="md" withBorder>
				<Title order={2} mb="md">
					Submit a New Product
				</Title>

				<Box component="form" onSubmit={form.onSubmit(handleSubmit)}>
					<Stack gap="md">
						<Divider label="Company Info" labelPosition="center" />

						<Group grow align="flex-start">
							<TextInput
								label="Company"
								disabled
								{...form.getInputProps("company")}
								required
							/>
							<TextInput
								label="Brand name"
								ref={fieldRefs.brand}
								placeholder="Enter brand name"
								value={form.values.brand}
								onChange={(event) => {
									form.setFieldValue("brand", event.currentTarget.value);
									if (form.errors.brand) {
										form.setFieldError("brand", null);
									}
								}}
								onBlur={() => {
									if (!form.values.brand) {
										form.setFieldError("brand", "Brand name is required");
									}
								}}
								error={form.errors.brand}
								withAsterisk
							/>
						</Group>

						<Divider label="Product Details" labelPosition="center" />

						<TextInput
							ref={fieldRefs.productName}
							label="Product Name"
							placeholder="Name"
							{...form.getInputProps("productName")}
							withAsterisk
						/>
						<TextInput
							label="Product Model Number"
							placeholder="Model"
							{...form.getInputProps("productModelNumber")}
						/>
						<Stack gap="md">
							<Box>
								<MultiSelect
									ref={fieldRefs.productCategory}
									withAsterisk
									label="Product Categories"
									description="Select one or more categories that best describe your product"
									placeholder={loadingCategories ? "Loading categories..." : "Search and select categories..."}
									data={productCategories}
									searchable
									clearable
									maxDropdownHeight={280}
									rightSection={loadingCategories && isEmpty(productCategories) ? <Loader size="xs" /> : undefined}
									{...form.getInputProps("productCategory")}
									styles={{
										pill: {
											backgroundColor: '#228be6',
											color: 'white',
											fontWeight: 500,
										}
									}}
								/>
								{form.values.productCategory.length > 0 && (
									<Text size="xs" c="dimmed" mt="xs">
										{form.values.productCategory.length} {form.values.productCategory.length === 1 ? 'category' : 'categories'} selected
									</Text>
								)}
								{categoryError && (
									<Text c="red" size="sm" mt="xs">
										{categoryError}
									</Text>
								)}
							</Box>

							{form.values.productCategory.length > 0 && (
								<Box>
									<MultiSelect
										label="Product Sub-Categories"
										description={
											isEmpty(subCategories)
												? "No sub-categories available for the selected categories"
												: "Optionally narrow down your product classification"
										}
										placeholder={
											loadingCategories
												? "Loading sub-categories..."
												: isEmpty(subCategories)
													? "No sub-categories available"
													: "Search and select sub-categories..."
										}
										data={subCategories}
										disabled={loadingCategories || isEmpty(subCategories)}
										searchable
										clearable
										maxDropdownHeight={280}
										rightSection={loadingCategories ? <Loader size="xs" /> : undefined}
										{...form.getInputProps("productSubCategory")}
										styles={{
											pill: {
												backgroundColor: '#15aabf',
												color: 'white',
												fontWeight: 500,
											}
										}}
									/>
									{form.values.productSubCategory.length > 0 && (
										<Text size="xs" c="dimmed" mt="xs">
											{form.values.productSubCategory.length} sub-{form.values.productSubCategory.length === 1 ? 'category' : 'categories'} selected
										</Text>
									)}
									{subCategoryError && (
										<Text c="red" size="sm" mt="xs">
											{subCategoryError}
										</Text>
									)}
								</Box>
							)}
						</Stack>

						<Group grow>
							<NumberInput
								label="Dante Input Channels"
								min={0}
								placeholder="2, 4, 8, 16, 32...anubi"
								{...form.getInputProps("inputChannels")}
							/>
							<NumberInput
								label="Dante Output Channels"
								min={0}
								placeholder="2, 4, 8, 16, 32...anubi"
								{...form.getInputProps("outputChannels")}
							/>
						</Group>

						<Group grow>
							<MultiSelect
								label="Bit Depth"
								placeholder={"Search Bit Depth"}
								data={[
									{ value: "16-bit", label: "16-bit" },
									{ value: "24-bit", label: "24-bit" },
									{ value: "32-bit", label: "32-bit" },
								]}
								searchable
								clearable
								maxDropdownHeight={280}
								{...form.getInputProps("bitDepth")}
								styles={{
									pill: {
										backgroundColor: '#228be6',
										color: 'white',
										fontWeight: 500,
									}
								}}
							/>

							<MultiSelect
								label="Sample Rate"
								placeholder={"Search Sample Rate"}
								data={[
									{ value: "32kHz", label: "32kHz" },
									{ value: "44.1kHz", label: "44.1kHz" },
									{ value: "48kHz", label: "48kHz" },
									{ value: "88.2kHz", label: "88.2kHz" },
									{ value: "96kHz", label: "96kHz" },
									{ value: "176.4kHz", label: "176.4kHz" },
									{ value: "192kHz", label: "192kHz" },
								]}
								searchable
								clearable
								maxDropdownHeight={280}
								{...form.getInputProps("sampleRate")}
								styles={{
									pill: {
										backgroundColor: '#228be6',
										color: 'white',
										fontWeight: 500,
									}
								}}
							/>
						</Group>

						<Checkbox
							label="AES67 Enabled"
							{...form.getInputProps("aes67Enabled", { type: "checkbox" })}
						/>
						<Checkbox
							label="Dante AV Enabled"
							{...form.getInputProps("danteAvEnabled", { type: "checkbox" })}
						/>

						<RichTextEditor
							label="Short Description"
							placeholder="Max 50 words"
							value={form.values.shortDescription || ""}
							onChange={(html) => {
								form.setFieldValue("shortDescription", html);
								form.setFieldError("shortDescription", null);
							}}
							maxWords={50}
							error={form.errors.shortDescription as string}
							minHeight={120}
						/>

						<RichTextEditor
							label="Long Description"
							placeholder="Max 150 words"
							value={form.values.longDescription || ""}
							onChange={(html) => {
								form.setFieldValue("longDescription", html);
								form.setFieldError("longDescription", null);
							}}
							maxWords={150}
							error={form.errors.longDescription as string}
							required
							minHeight={200}
						/>


						<Divider label="Links" labelPosition="center" />

						<TextInput
							label="Website URL"
							placeholder="https://"
							{...form.getInputProps("websiteUrl")}
							withAsterisk
							error={form.errors.websiteUrl}
						/>
						<TextInput
							label="Support Links"
							placeholder="https://support.example.com, https://docs.example.com"
							{...form.getInputProps("supportLinks")}
							withAsterisk
							error={form.errors.supportLinks}
						/>

						<Divider label="Submitted By" labelPosition="center" />

						<Group grow>
							<TextInput
								label="Your Name"
								disabled
								{...form.getInputProps("submitterName")}
							/>
							<TextInput
								label="Your Email"
								disabled
								{...form.getInputProps("submitterEmail")}
							/>
						</Group>
						<input type="hidden" {...form.getInputProps("submitterId")} />

						<Divider label="Product Images" labelPosition="center" />


						{/* FEATURED IMAGE */}
						<Stack gap="xs">
							<Text size="sm" fw={600}>
								Featured Image{" "}
								<Text span c="red">
									*
								</Text>
							</Text>

							{form.values.featuredImage ? (
								<Box
									p="xs"
									style={{
										border: "1px solid #ced4da",
										borderRadius: 8,
										backgroundColor: "white",
									}}
								>
									<Group justify="space-between" align="center">
										<Group gap="sm" align="center">
											<Image
												src={getPreviewUrl(form.values.featuredImage)}
												alt="Featured preview"
												w={50}
												h={50}
												fit="cover"
												radius="sm"
											/>
											<Text size="sm" fw={500}>
												{form.values.featuredImage.name} (
												{(form.values.featuredImage.size / 1024 / 1024).toFixed(
													2,
												)}{" "}
												MB)
											</Text>
										</Group>
										<Group gap="xs">
											<Button
												size="xs"
												variant="light"
												color="blue"
												onClick={() => {
													setModalImage(
														getPreviewUrl(form.values.featuredImage),
													);
													setModalImageName(form.values.featuredImage.name);
													open();
												}}
											>
												View
											</Button>
											<Button
												size="xs"
												color="red"
												variant="outline"
												onClick={() =>
													form.setFieldValue("featuredImage", null)
												}
												disabled={loading}
											>
												Remove
											</Button>
										</Group>
									</Group>
								</Box>
							) : (
								<Dropzone
									onDrop={(files) => {
										if (loading) return; // Prevent file changes during submission
										if (files.length > 1) {
											form.setFieldError(
												"featuredImage",
												"Only one image is allowed for Featured Image",
											);
										} else {
											form.setFieldError("featuredImage", null);
											form.setFieldValue("featuredImage", files[0]);
										}
									}}
									onReject={(fileRejections) => {
										if (loading) return; // Prevent file changes during submission
										const tooLarge = fileRejections.some(({ errors }) =>
											errors.some((e) => e.code === "file-too-large"),
										);
										form.setFieldError(
											"featuredImage",
											tooLarge
												? "Image is greater than 5MB, too large"
												: "Invalid image",
										);
									}}
									maxSize={5 * 1024 ** 2}
									accept={IMAGE_MIME_TYPE}
									disabled={loading}
									style={{
										borderRadius: 12,
										backgroundColor: loading ? "#f1f3f4" : "#f8f9fa",
										border: "2px dashed #ced4da",
										opacity: loading ? 0.6 : 1,
										cursor: loading ? "not-allowed" : "pointer",
									}}
								>
									<Group justify="center" gap="xs" style={{ minHeight: 100 }}>
										<HiOutlineUpload size={40} />
										<Text ta="center">
											Drag & drop or click to upload <strong>Featured</strong>{" "}
											Image
											<br />
											<small>(PNG/JPG, Max 5MB)</small>
										</Text>
									</Group>
								</Dropzone>
							)}

							{form.errors.featuredImage && (
								<Text c="red" size="sm" ta="center">
									{form.errors.featuredImage}
								</Text>
							)}
						</Stack>

						{/* ----- Additional Images ----- */}
						<Stack gap="xs" mt="md">
							<Text size="sm" fw={600}>
								Additional Images
							</Text>

							{form.values.additionalImages.length > 0 && (
								<Card withBorder radius="md" padding="sm" bg="white">
									<Stack gap="xs">
										{form.values.additionalImages.map((file, index) => (
											<Group key={index} justify="space-between" align="center">
												<Group gap="sm" align="center">
													<Image
														src={getPreviewUrl(file)}
														alt={`Additional preview ${index + 1}`}
														w={50}
														h={50}
														fit="cover"
														radius="sm"
													/>
													<Text size="sm" fw={500} truncate>
														{file.name} ({(file.size / 1024 / 1024).toFixed(2)}{" "}
														MB)
													</Text>
												</Group>
												<Group gap="xs">
													<Button
														size="xs"
														variant="light"
														color="blue"
														onClick={() => {
															setModalImage(URL.createObjectURL(file));
															setModalImageName(file.name);
															open();
														}}
													>
														View
													</Button>
													<Button
														size="xs"
														variant="outline"
														color="red"
														onClick={() => {
															const updated =
																form.values.additionalImages.filter(
																	(_, i) => i !== index,
																);
															form.setFieldValue("additionalImages", updated);
															setError(null);
														}}
														disabled={loading}
													>
														Remove
													</Button>
												</Group>
											</Group>
										))}
									</Stack>
								</Card>
							)}

							<Dropzone
								onDrop={(files) => {
									if (loading) return; // Prevent file changes during submission
									const existingFiles = form.values.additionalImages;
									const duplicates = files.filter((file) =>
										existingFiles.some(
											(f) => f.name === file.name && f.size === file.size,
										),
									);
									const newFiles = files.filter(
										(file) =>
											!existingFiles.some(
												(f) => f.name === file.name && f.size === file.size,
											),
									);

									if (duplicates.length > 0) {
										const duplicateName = duplicates
											.map((f) => f.name)
											.join(", ");
										setAdditionalImagesError(
											`The file ${duplicateName} ${duplicates.length > 1 ? "are" : "is"
											} already selected as additional image.`,
										);
									} else {
										setAdditionalImagesError(null);
									}

									form.setFieldValue("additionalImages", [
										...existingFiles,
										...newFiles,
									]);
								}}
								onReject={(fileRejections) => {
									if (loading) return; // Prevent file changes during submission
									const tooLargeFiles = fileRejections
										.filter(({ errors }) =>
											errors.some((e) => e.code === "file-too-large"),
										)
										.map(({ file }) => file.name);

									if (tooLargeFiles.length > 0) {
										const fileList = tooLargeFiles.join(", ");
										setAdditionalImagesError(
											`The following file${tooLargeFiles.length > 1 ? "s are" : " is"
											} too large (over 5MB): ${fileList}`,
										);
									} else {
										setAdditionalImagesError("One or more files are invalid.");
									}
								}}
								maxSize={5 * 1024 ** 2}
								accept={IMAGE_MIME_TYPE}
								multiple
								disabled={loading}
								style={{
									borderRadius: 12,
									backgroundColor: loading ? "#f1f3f4" : "#f8f9fa",
									border: "2px dashed #ced4da",
									opacity: loading ? 0.6 : 1,
									cursor: loading ? "not-allowed" : "pointer",
								}}
							>
								<Group justify="center" gap="xs" style={{ minHeight: 100 }}>
									<HiOutlineUpload size={40} />
									<Text ta="center">
										Drag & drop or click to upload <strong>Additional</strong>{" "}
										Images
										<br />
										<small>(PNG/JPG, Max 5MB each)</small>
									</Text>
								</Group>
							</Dropzone>
							{additionalImagesError && (
								<Text c="red" size="sm" ta="center">
									{additionalImagesError}
								</Text>
							)}
						</Stack>

						<Group justify="flex-end" mt="sm">
							{error && (
								<Alert color="red" mb="xs">
									{error}
								</Alert>
							)}
							<Button
								type="submit"
								loading={loading}
								disabled={loadingCategories || loading}
								leftSection={
									loading ? <Loader size="xs" color="white" /> : null
								}
							>
								{loading ? "Submitting..." : "Submit Product"}
							</Button>
						</Group>
					</Stack>
				</Box>
			</Card>

			<Modal
				opened={opened}
				onClose={() => {
					setZoomed(false);
					close();
				}}
				title={modalImageName ? `Viewing: ${modalImageName}` : "View Image"}
				size="auto"
				centered
				overlayProps={{ blur: 3 }}
				styles={{
					body: {
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						cursor: zoomed ? "zoom-out" : "zoom-in",
					},
				}}
			>
				{modalImage && (
					<Image
						src={modalImage}
						alt="Full image"
						style={{
							maxWidth: zoomed ? "none" : "100%",
							maxHeight: zoomed ? "none" : "80vh",
							transform: zoomed ? "scale(1.5)" : "scale(1)",
							transition: "transform 0.3s ease",
						}}
						onClick={() => setZoomed(!zoomed)}
					/>
				)}
			</Modal>
		</>
	);
};

export default ProductForm;